import React, { useEffect, useState } from "react";
import { OrderFormType } from "../../interfaces/orderFormType";
import {
  determineOrderFormTypes,
  getOrderFormTypes,
  uploadCandidateOrderForm,
} from "../../services/orderFormService";
import { useViewOrderContext } from "../../contexts/ViewOrderContext";

interface UploadCandidateFormProps {
  onClose: (message?: string) => void;
}

const UploadCandidateForm: React.FC<UploadCandidateFormProps> = ({
  onClose,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [availableOrderFormTypes, setAvailableOrderFormTypes] = useState<
    OrderFormType[] | null
  >(null);
  const [selectedFormType, setSelectedFormType] =
    useState<OrderFormType | null>(null);

  const {
    client,
    order,
    referenceOrder,
    criminalOrder,
    subscriptionOrder,
    orderFormTypes,
  } = useViewOrderContext();

  useEffect(() => {
    const getDetails = async () => {
      try {
        setLoadingSpinner(true);
        const orderFormTypesResponse = await getOrderFormTypes();

        const { forms } = determineOrderFormTypes(
          client,
          orderFormTypesResponse,
          referenceOrder,
          criminalOrder,
          subscriptionOrder,
        );

        setAvailableOrderFormTypes(forms);
      } catch (error) {
        console.error("Error getting order form types:", error);
      } finally {
        setLoadingSpinner(false);
      }
    };
    getDetails();
  }, [
    client,
    orderFormTypes,
    referenceOrder,
    criminalOrder,
    subscriptionOrder,
    order,
  ]);

  const handleUploadCandidateForm = async () => {
    if (!file || !selectedFormType) return;

    try {
      setLoadingSpinner(true);
      await uploadCandidateOrderForm(order, selectedFormType, file);
      window.location.reload();
    } catch (error) {
      console.error("Error handling file upload:", error);
      setErrorMessage("Error uploading candidate form.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleGrabForm = async () => {
    try {
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = ".pdf";
      fileInput.click();
      fileInput.onchange = async (event: any) => {
        const file = event.target.files[0];
        if (!file) return;
        setFile(file);
      };
      setErrorMessage(
        "If there is a form uploaded of the same type, you will be replacing it.",
      );
    } catch (error) {
      console.error("Error handling file upload:", error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <>
      <div className="background-mask" onClick={() => onClose()}></div>
      <div className="filter-popup">
        <div className="close-button-container">
          <button className="close-button" onClick={() => onClose()}>
            <i className="bi bi-x-circle"></i>
          </button>
        </div>
        <div className="filter-header">
          <h2>Upload Candidate Form</h2>
          <div>Please choose a form type.</div>
        </div>
        <br></br>
        <div className="dialog-container">
          <div className="form-group">
            <select
              id="formTypeSelect"
              className="form-control"
              value={selectedFormType?.description || ""}
              onChange={(e) => {
                const selected = availableOrderFormTypes?.find(
                  (type) => type.description === e.target.value,
                );
                setSelectedFormType(selected || null);
              }}
            >
              <option value="" disabled>
                -- Select Form Type --
              </option>
              {availableOrderFormTypes?.map((formType) => (
                <option
                  key={formType.orderFormTypeID}
                  value={formType.description}
                >
                  {formType.description}
                </option>
              ))}
            </select>
          </div>
          <div className="action-buttons">
            <button className="smbtn btn-grey" onClick={() => handleGrabForm()}>
              Upload Candidate Form
            </button>
            {file && (
              <a
                href={URL.createObjectURL(file)}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Form
                <i className="btn-icon bi bi-box-arrow-up-right"></i>
              </a>
            )}
          </div>

          {errorMessage && (
            <div className="error" style={{ textAlign: "center" }}>
              {errorMessage}
            </div>
          )}
        </div>
        <div className="filter-actions">
          <button
            className={`smbtn btn-green ${
              !file || !selectedFormType ? "disabled" : ""
            }`}
            onClick={handleUploadCandidateForm}
            disabled={!file || !selectedFormType}
          >
            Save to Order
          </button>
        </div>
      </div>
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </>
  );
};

export default UploadCandidateForm;
